import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Button,
  Divider,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link
} from '@mui/material';
import EmailAutocompleteTextField from '../../EmailAutocompleteTextField';
import ForgotPasswordDialog from '../../ForgotPasswordDialog';
import {
  Person,
  Email,
  Phone,
  Login,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../../contexts/BookingContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useQuestionnaire } from '../../../contexts/QuestionnaireContext';

const CustomerInfoStep = ({ isModal = false }) => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { saveFormProgress, saveStepProgress } = useQuestionnaire();
  const {
    bookingState,
    updateCustomerInfo,
    clearErrors,
    setCurrentStep,
    markStepCompleted,
    setErrors,
    validateCurrentStep,
    currentStepData,
    availableSteps
  } = useBooking();

  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);


  const contactPreferences = [
    { value: 'email', label: 'Email', icon: <Email /> },
    { value: 'phone', label: 'Phone Call', icon: <Phone /> },
    { value: 'text', label: 'Text Message', icon: <Phone /> }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFieldChange = (field) => (event) => {
    const value = typeof event === 'object' && event.target ? event.target.value : event;
    updateCustomerInfo({ [field]: value });
    clearErrors([field]);
    
    // Save questionnaire progress for lead capture
    saveFormProgress('customer', {
      ...bookingState.customerInfo,
      [field]: value
    });
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Apply formatting
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  const handlePhoneChange = (event) => {
    const formatted = formatPhoneNumber(event.target.value);
    updateCustomerInfo({ phone: formatted });
    clearErrors(['phone']);
    
    // Save questionnaire progress for lead capture
    saveFormProgress('customer', {
      ...bookingState.customerInfo,
      phone: formatted
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const passwordsMatch = () => {
    const { password, passwordConfirm } = bookingState.customerInfo;
    return !password || !passwordConfirm || password === passwordConfirm;
  };

  const handleSignIn = async () => {
    setIsLoading(true);
    setLoginError('');

    try {
      const response = await login(bookingState.customerInfo.email, bookingState.customerInfo.password);
      if (response && response.success) {
        // Mark current step as completed and move to next step
        markStepCompleted(bookingState.currentStep);
        setCurrentStep(bookingState.currentStep + 1);
      } else {
        setLoginError(response?.error || 'Invalid email or password');
      }
    } catch (error) {
      setLoginError('Failed to sign in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    // Clear any existing errors
    clearErrors();
    
    // Validate the current step
    const validation = validateCurrentStep();
    
    if (validation.isValid) {
      // Mark current step as completed and move to next step
      markStepCompleted(bookingState.currentStep);
      setCurrentStep(bookingState.currentStep + 1);
    } else {
      // Show validation errors
      setErrors(validation.errors);
    }
  };

  // If user is authenticated, show completed state
  if (user) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Contact Information
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          You are signed in and your contact information is confirmed
        </Typography>

        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2">
            ✓ You are signed in as <strong>{user.firstName} {user.lastName}</strong> ({user.email})
          </Typography>
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person /> Your Information
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{user.firstName} {user.lastName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{user.phoneNumber || user.phone || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">ZIP Code</Typography>
                <Typography variant="body1">{user.zipCode || 'Not provided'}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {!isModal && (
        <>
          <Typography variant="h5" gutterBottom>
            Contact Information
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We'll use this information to confirm your booking and provide updates
          </Typography>

          {/* Login Options */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2">
                Have an account? Sign in to pre-fill your information and track your bookings.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Button
                  size="small"
                  startIcon={<Login />}
                  onClick={() => navigate('/login', { state: { returnTo: '/booking' } })}
                >
                  Sign In
                </Button>
              </Box>
            </Box>
          </Alert>
        </>
      )}
      {isModal && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Fill out the form below to create your account and proceed to booking
        </Typography>
      )}

      {/* Contact Information Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person /> Personal Information
          </Typography>
          
          
          <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <EmailAutocompleteTextField
                fullWidth
                type="email"
                label="Email Address"
                name="email"
                value={bookingState.customerInfo.email || ''}
                onChange={(value) => {
                  // Directly use the value since EmailAutocompleteTextField already handles the event
                  handleFieldChange('email')(value);
                }}
                error={!!bookingState.errors.email}
                helperText={null}
                required
                placeholder="john@example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={bookingState.customerInfo.zipCode || ''}
                onChange={handleFieldChange('zipCode')}
                error={!!bookingState.errors.zipCode}
                helperText={null}
                required
                placeholder="12345"
                inputProps={{ maxLength: 5, pattern: '[0-9]{5}' }}
              />
            </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={bookingState.customerInfo.firstName}
                onChange={handleFieldChange('firstName')}
                error={!!bookingState.errors.firstName}
                helperText={bookingState.errors.firstName}
                required
                placeholder="John"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={bookingState.customerInfo.lastName}
                onChange={handleFieldChange('lastName')}
                error={!!bookingState.errors.lastName}
                helperText={bookingState.errors.lastName}
                required
                placeholder="Doe"
              />
            </Grid>
            

            
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={bookingState.customerInfo.phone}
                onChange={handlePhoneChange}
                error={!!bookingState.errors.phone}
                helperText={
                  bookingState.errors.phone || 
                  'For booking updates and coordination with your cleaning team'
                }
                required
                placeholder="(555) 123-4567"
                inputProps={{ maxLength: 14 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Create Password"
                value={bookingState.customerInfo.password || ''}
                onChange={handleFieldChange('password')}
                error={!!bookingState.errors.password || !!loginError}
                helperText={null}
                required
                placeholder="Create a secure password"
                inputProps={{ minLength: 6 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                type={showPasswordConfirm ? 'text' : 'password'}
                label="Confirm Password"
                value={bookingState.customerInfo.passwordConfirm || ''}
                onChange={handleFieldChange('passwordConfirm')}
                error={!!bookingState.errors.passwordConfirm}
                helperText={
                  bookingState.errors.passwordConfirm || 
                  'Re-enter your password to confirm'
                }
                required
                placeholder="Confirm your password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password confirmation visibility"
                        onClick={handleClickShowPasswordConfirm}
                        edge="end"
                      >
                        {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            

            
            {/* Password Mismatch Warning */}
            {bookingState.customerInfo.password && bookingState.customerInfo.passwordConfirm && !passwordsMatch() && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Passwords do not match. Please make sure both password fields are identical.
                </Alert>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Communication Preferences - hide in modal mode */}
      {!isModal && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              How would you prefer we contact you?
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Preferred Contact Method</InputLabel>
              <Select
                value={bookingState.customerInfo.contactPreference}
                onChange={handleFieldChange('contactPreference')}
                label="Preferred Contact Method"
              >
                {contactPreferences.map((pref) => (
                  <MenuItem key={pref.value} value={pref.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {pref.icon}
                      {pref.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Privacy Notice - hide in modal mode */}
      {!isModal && (
        <Card sx={{ backgroundColor: 'grey.50' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Privacy & Security
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Your information is secure and will only be used for:
            </Typography>
            
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Booking confirmations and service updates
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Coordinating with your cleaning team
                </Typography>
              </li>
              <li>
                <Typography variant="body2" color="text.secondary">
                  Quality assurance and customer support
                </Typography>
              </li>
            </ul>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary">
              We never share your information with third parties. 
              View our{' '}
              <a href="/privacy" target="_blank" style={{ color: 'inherit' }}>
                Privacy Policy
              </a>{' '}
              for more details.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Validation Summary */}
      {Object.keys(bookingState.errors).some(key => 
        ['firstName', 'lastName', 'email', 'phone', 'password', 'passwordConfirm', 'zipCode'].includes(key)
      ) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Please complete all required fields to continue.
        </Alert>
      )}

      {/* Continue Button - Only show in non-modal mode */}
      {!isModal && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleContinue}
            endIcon={<ArrowForward />}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            Continue
          </Button>
        </Box>
      )}

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
    </Box>
  );
};

export default CustomerInfoStep;